import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTheme } from '../contexts/ThemeContext';
import { roleConfigService, RolePageConfigMap, PageDefinition } from '../services/roleConfig';
import { savePageAccessConfig } from '../utils/roleAccess';
import { Shield, Save, RotateCcw, CheckSquare, Square, Plus, Edit, Trash2, X, GripVertical } from 'lucide-react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { PageSkeleton } from '../components/LoadingStates';

interface PageFormData {
  key: string;
  name: string;
  href: string;
  sortOrder: number;
}

export const RolePageConfig: React.FC = () => {
  const { currentTheme } = useTheme();
  const queryClient = useQueryClient();
  const [config, setConfig] = useState<RolePageConfigMap>({});
  const [showPageModal, setShowPageModal] = useState(false);
  const [editingPage, setEditingPage] = useState<PageDefinition | null>(null);

  const primary = currentTheme.colors.primary;
  const text    = currentTheme.colors.text;
  const bg      = currentTheme.colors.background;
  const border  = currentTheme.colors.border;

  const pageForm = useForm<PageFormData>();

  const { data: roles = [], isLoading: rolesLoading } = useQuery(
    ['roles'],
    roleConfigService.getRoles,
    { staleTime: 5 * 60 * 1000 }
  );

  const { data: pages = [], isLoading: pagesLoading } = useQuery(
    ['app-pages'],
    roleConfigService.getPages,
    { staleTime: 0 }
  );

  const { data: remoteConfig, isLoading: configLoading } = useQuery(
    ['role-page-config'],
    roleConfigService.getConfig,
    { staleTime: 0 }
  );

  useEffect(() => {
    if (remoteConfig) { setConfig(remoteConfig); setConfigDirty(false); }
  }, [remoteConfig]);

  // --- Drag-to-reorder page sequence ---
  const [orderedPages, setOrderedPages] = useState<PageDefinition[]>([]);
  const [orderDirty, setOrderDirty] = useState(false);
  const [configDirty, setConfigDirty] = useState(false);
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);
  const dragIndex = useRef<number | null>(null);

  useEffect(() => {
    setOrderedPages([...pages].sort((a, b) => a.sortOrder - b.sortOrder));
    setOrderDirty(false);
  }, [pages]);

  const moveRow = (from: number, to: number) => {
    setOrderedPages(prev => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setOrderDirty(true);
  };

  // A single Save persists both the access matrix and any reordered sequence.
  const saveMutation = useMutation(
    async () => {
      if (orderDirty) {
        await Promise.all(
          orderedPages
            .map((p, i) => (p.sortOrder === i + 1 ? null : roleConfigService.updatePage(p.id, { sortOrder: i + 1 })))
            .filter(Boolean) as Promise<unknown>[]
        );
      }
      return roleConfigService.saveConfig(config);
    },
    {
      onSuccess: (saved) => {
        savePageAccessConfig(saved);
        if (orderDirty) { queryClient.invalidateQueries(['app-pages']); setOrderDirty(false); }
        setConfigDirty(false);
        toast.success('Saved');
      },
      onError: () => { toast.error('Failed to save'); },
    }
  );

  const createPageMutation = useMutation(roleConfigService.createPage, {
    onSuccess: () => {
      queryClient.invalidateQueries(['app-pages']);
      toast.success('Page added successfully');
      setShowPageModal(false);
      pageForm.reset();
    },
    onError: (err: any) => { toast.error(err.response?.data?.message || 'Failed to add page'); },
  });

  const updatePageMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<PageDefinition> }) =>
      roleConfigService.updatePage(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['app-pages']);
        toast.success('Page updated');
        setShowPageModal(false);
        setEditingPage(null);
        pageForm.reset();
      },
      onError: (err: any) => { toast.error(err.response?.data?.message || 'Failed to update page'); },
    }
  );

  const deletePageMutation = useMutation(roleConfigService.deletePage, {
    onSuccess: () => {
      queryClient.invalidateQueries(['app-pages']);
      toast.success('Page deleted');
    },
    onError: (err: any) => { toast.error(err.response?.data?.message || 'Failed to delete page'); },
  });

  const isLoading = rolesLoading || pagesLoading || configLoading;
  const dirty = orderDirty || configDirty;

  const toggleAccess = (pageKey: string, roleKey: string) => {
    setConfigDirty(true);
    setConfig(prev => {
      const current = prev[pageKey] || [];
      const updated = current.includes(roleKey)
        ? current.filter(r => r !== roleKey)
        : [...current, roleKey];
      return { ...prev, [pageKey]: updated };
    });
  };

  const toggleRow = (pageKey: string) => {
    setConfigDirty(true);
    const all = roles.map(r => r.key);
    const current = config[pageKey] || [];
    const allOn = all.every(r => current.includes(r));
    setConfig(prev => ({ ...prev, [pageKey]: allOn ? [] : [...all] }));
  };

  const toggleColumn = (roleKey: string) => {
    setConfigDirty(true);
    const allOn = pages.every(pg => (config[pg.key] || []).includes(roleKey));
    setConfig(prev => {
      const next = { ...prev };
      pages.forEach(pg => {
        const cur = next[pg.key] || [];
        next[pg.key] = allOn ? cur.filter(r => r !== roleKey) : cur.includes(roleKey) ? cur : [...cur, roleKey];
      });
      return next;
    });
  };

  const handleReset = () => {
    if (remoteConfig) {
      setConfig({ ...remoteConfig });
      setConfigDirty(false);
    }
    setOrderedPages([...pages].sort((a, b) => a.sortOrder - b.sortOrder));
    setOrderDirty(false);
    toast.success('Reset to last saved');
  };

  const openAddPage = () => {
    setEditingPage(null);
    pageForm.reset({ key: '', name: '', href: '/', sortOrder: pages.length + 1 });
    setShowPageModal(true);
  };

  const openEditPage = (page: PageDefinition) => {
    setEditingPage(page);
    pageForm.reset({ key: page.key, name: page.name, href: page.href, sortOrder: page.sortOrder });
    setShowPageModal(true);
  };

  const onPageSubmit = (data: PageFormData) => {
    if (editingPage) {
      updatePageMutation.mutate({ id: editingPage.id, data });
    } else {
      createPageMutation.mutate(data);
    }
  };

  const handleDeletePage = (page: PageDefinition) => {
    if (window.confirm(`Delete page "${page.name}"? This cannot be undone.`)) {
      deletePageMutation.mutate(page.id);
    }
  };

  const isPageModalLoading = createPageMutation.isLoading || updatePageMutation.isLoading;

  if (isLoading) return <PageSkeleton type="table" />;

  return (
    <div className="p-4 sm:p-6 space-y-5" style={{ backgroundColor: bg.primary, minHeight: '100vh' }}>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl" style={{ backgroundColor: primary[100] }}>
            <Shield className="h-6 w-6" style={{ color: primary[600] }} />
          </div>
          <div>
            <h1 className="text-xl font-bold" style={{ color: text.primary }}>
              Role Page Access
            </h1>
            <p className="text-sm mt-0.5" style={{ color: text.secondary }}>
              Configure which roles can access each page. Add new pages as your app grows.
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={openAddPage}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg text-white transition-colors hover:opacity-90"
            style={{ backgroundColor: primary[500] }}
          >
            <Plus className="h-4 w-4" />
            Add Page
          </button>
          <button
            onClick={handleReset}
            disabled={!dirty || saveMutation.isLoading}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition-colors hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ borderColor: border.primary, color: text.secondary, backgroundColor: bg.primary }}
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </button>
          <button
            onClick={() => saveMutation.mutate()}
            disabled={!dirty || saveMutation.isLoading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg text-white transition-colors hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: primary[600] }}
            title={dirty ? 'Save access + page order' : 'Make a change to enable'}
          >
            <Save className="h-4 w-4" />
            {saveMutation.isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Matrix */}
      <div className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)] overflow-auto shadow-sm" style={{ borderColor: border.primary }}>
        <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: primary[50] }}>
              <th
                className="text-left px-4 py-3 font-semibold"
                style={{
                  color: text.primary,
                  borderBottom: `1px solid ${border.primary}`,
                  borderRight: `1px solid ${border.primary}`,
                  minWidth: 250,
                  position: 'sticky',
                  left: 0,
                  backgroundColor: primary[50],
                  zIndex: 10,
                }}
              >
                <span className="inline-flex items-center gap-2"><span className="w-6 text-center">#</span>Page</span>
              </th>

              {roles.map(role => {
                const colAllOn = pages.every(pg => (config[pg.key] || []).includes(role.key));
                return (
                  <th
                    key={role.key}
                    className="px-3 py-3 font-semibold text-center"
                    style={{
                      color: text.primary,
                      borderBottom: `1px solid ${border.primary}`,
                      borderRight: `1px solid ${border.primary}`,
                      minWidth: 90,
                    }}
                  >
                    <div className="flex flex-col items-center gap-1.5">
                      <span className="text-xs font-semibold tracking-wide uppercase" style={{ color: primary[700] }}>
                        {role.label}
                      </span>
                      <button
                        onClick={() => toggleColumn(role.key)}
                        className="transition-opacity hover:opacity-70"
                      >
                        {colAllOn
                          ? <CheckSquare className="h-4 w-4" style={{ color: primary[600] }} />
                          : <Square className="h-4 w-4" style={{ color: text.secondary }} />}
                      </button>
                    </div>
                  </th>
                );
              })}

              <th className="px-3 py-3 text-center" style={{ color: text.secondary, borderBottom: `1px solid ${border.primary}`, borderRight: `1px solid ${border.primary}`, minWidth: 60, fontSize: 11 }}>
                All
              </th>
              <th className="px-3 py-3 text-center" style={{ color: text.secondary, borderBottom: `1px solid ${border.primary}`, minWidth: 80, fontSize: 11 }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {orderedPages.map((page, idx) => {
              const pageRoles = config[page.key] || [];
              const rowAllOn = roles.every(r => pageRoles.includes(r.key));

              return (
                <tr
                  key={page.key}
                  style={{ backgroundColor: idx % 2 === 0 ? bg.primary : primary[50] + '55', opacity: draggingIdx === idx ? 0.4 : 1 }}
                  draggable
                  onDragStart={() => { dragIndex.current = idx; setDraggingIdx(idx); }}
                  onDragOver={e => {
                    e.preventDefault();
                    const from = dragIndex.current;
                    if (from !== null && from !== idx) { moveRow(from, idx); dragIndex.current = idx; setDraggingIdx(idx); }
                  }}
                  onDragEnd={() => { dragIndex.current = null; setDraggingIdx(null); }}
                >
                  <td
                    className="px-4 py-3 font-medium"
                    style={{
                      color: text.primary,
                      borderRight: `1px solid ${border.primary}`,
                      borderBottom: `1px solid ${border.primary}`,
                      position: 'sticky',
                      left: 0,
                      backgroundColor: idx % 2 === 0 ? bg.primary : primary[50] + '55',
                      zIndex: 5,
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <GripVertical className="h-4 w-4 shrink-0 cursor-grab active:cursor-grabbing" style={{ color: text.secondary }} />
                      <span className="w-6 text-center text-xs font-bold" style={{ color: text.secondary }}>{idx + 1}</span>
                      <div>
                        <div>{page.name}</div>
                        <div className="text-xs mt-0.5" style={{ color: text.secondary }}>{page.href}</div>
                      </div>
                    </div>
                  </td>

                  {roles.map(role => {
                    const checked = pageRoles.includes(role.key);
                    return (
                      <td
                        key={role.key}
                        className="px-3 py-3 text-center"
                        style={{ borderRight: `1px solid ${border.primary}`, borderBottom: `1px solid ${border.primary}` }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleAccess(page.key, role.key)}
                          className="h-4 w-4 rounded cursor-pointer"
                          style={{ accentColor: primary[600] }}
                        />
                      </td>
                    );
                  })}

                  <td className="px-3 py-3 text-center" style={{ borderBottom: `1px solid ${border.primary}`, borderRight: `1px solid ${border.primary}` }}>
                    <button onClick={() => toggleRow(page.key)} className="transition-opacity hover:opacity-70 mx-auto block">
                      {rowAllOn
                        ? <CheckSquare className="h-4 w-4" style={{ color: primary[600] }} />
                        : <Square className="h-4 w-4" style={{ color: text.secondary }} />}
                    </button>
                  </td>

                  <td className="px-3 py-3 text-center" style={{ borderBottom: `1px solid ${border.primary}` }}>
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => openEditPage(page)}
                        className="p-1.5 rounded hover:opacity-70"
                        style={{ color: primary[600], backgroundColor: primary[50] }}
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handleDeletePage(page)}
                        className="p-1.5 rounded hover:opacity-70"
                        style={{ color: '#dc2626', backgroundColor: '#fef2f2' }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs" style={{ color: text.secondary }}>
        Configuration is stored in the database. Users need to re-login or refresh for nav changes to take effect.
      </p>

      {/* Add / Edit Page Modal */}
      {showPageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Shield className="h-5 w-5" style={{ color: primary[600] }} />
                {editingPage ? 'Edit Page' : 'Add New Page'}
              </h2>
              <button onClick={() => { setShowPageModal(false); setEditingPage(null); pageForm.reset(); }} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={pageForm.handleSubmit(onPageSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Key * <span className="text-xs text-gray-400">(unique identifier, e.g. "inventory")</span>
                </label>
                <input
                  {...pageForm.register('key', { required: 'Key is required' })}
                  type="text" placeholder="e.g. inventory"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none text-sm"
                />
                {pageForm.formState.errors.key && (
                  <p className="mt-1 text-xs text-red-600">{pageForm.formState.errors.key.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
                <input
                  {...pageForm.register('name', { required: 'Name is required' })}
                  type="text" placeholder="e.g. Inventory"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none text-sm"
                />
                {pageForm.formState.errors.name && (
                  <p className="mt-1 text-xs text-red-600">{pageForm.formState.errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL / href * <span className="text-xs text-gray-400">(e.g. /inventory)</span>
                </label>
                <input
                  {...pageForm.register('href', { required: 'URL is required' })}
                  type="text" placeholder="/inventory"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none text-sm"
                />
                {pageForm.formState.errors.href && (
                  <p className="mt-1 text-xs text-red-600">{pageForm.formState.errors.href.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                <input
                  {...pageForm.register('sortOrder', { valueAsNumber: true })}
                  type="number" min={1}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:outline-none text-sm"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setShowPageModal(false); setEditingPage(null); pageForm.reset(); }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">
                  Cancel
                </button>
                <button type="submit" disabled={isPageModalLoading}
                  className="px-4 py-2 text-white rounded-lg btn-primary text-sm disabled:opacity-60"
                  style={{ backgroundColor: primary[600] }}>
                  {isPageModalLoading ? 'Saving...' : editingPage ? 'Update' : 'Add Page'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
